import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import LessonOptionStatusChip from '../../components/host/LessonOptionStatusChip';
import TutorLessonSessionTypeChip from '../../components/tutor/TutorLessonSessionTypeChip';
import type { LessonOptionStatus, LessonOptionSummaryResponse } from '../../models/LessonOption';
import { OrganizationType } from '../../models/Organization';
import {
  nextLessonOptionCopyName,
  type LessonOptionDuplicateLocationState,
} from '../../utils/lessonOptionDuplicate';

type ListTab = 'offers' | 'archive';

type LessonOptionsLocationState = {
  flashMessage?: string;
};

const formatMoneyAmount = (amount: number, currency: string, language: string): string =>
  new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);

const HostLessonOptionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [listTab, setListTab] = useState<ListTab>('offers');
  const [organizationType, setOrganizationType] = useState<OrganizationType | undefined>();
  const [offerOptions, setOfferOptions] = useState<LessonOptionSummaryResponse[]>([]);
  const [archivedOptions, setArchivedOptions] = useState<LessonOptionSummaryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuOption, setMenuOption] = useState<LessonOptionSummaryResponse | null>(null);
  const [removeTarget, setRemoveTarget] = useState<LessonOptionSummaryResponse | null>(null);
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const options = listTab === 'archive' ? archivedOptions : offerOptions;
  const isIndividualTutorOrg = organizationType === OrganizationType.INDIVIDUAL_TUTOR;

  useEffect(() => {
    let cancelled = false;
    void hostApiClient
      .getOrganization()
      .then((organization) => {
        if (!cancelled) {
          setOrganizationType(organization.organizationType);
        }
      })
      .catch((err) => {
        console.error('Error fetching organization type for lesson options list:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const state = location.state as LessonOptionsLocationState | null;
    if (state?.flashMessage) {
      setSnackbar({ open: true, message: state.flashMessage, severity: 'success' });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchOptions = useCallback(async () => {
    try {
      setLoading(true);
      const offersResult = await hostApiClient.listLessonOptions();
      setOfferOptions(offersResult);
      setError(null);
      try {
        const archived = await hostApiClient.listLessonOptions(['ARCHIVED']);
        setArchivedOptions(archived);
      } catch (archivedErr) {
        // Older APIs reject ARCHIVED until the status migration is deployed.
        console.warn('Error fetching archived host lesson options:', archivedErr);
        setArchivedOptions([]);
      }
    } catch (err) {
      console.error('Error fetching host lesson options:', err);
      setError(t('hostLessonOptions.fetchError', 'Failed to load lesson options'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const patchOfferStatus = (lessonOptionId: string, status: LessonOptionStatus) => {
    setOfferOptions((prev) =>
      prev.map((option) => (option.id === lessonOptionId ? { ...option, status } : option))
    );
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuOption(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, option: LessonOptionSummaryResponse) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuOption(option);
  };

  const handleActivate = async (event: React.MouseEvent | null, lessonOptionId: string) => {
    event?.stopPropagation();
    closeMenu();
    try {
      setStatusLoadingId(lessonOptionId);
      const updated = await hostApiClient.activateLessonOption(lessonOptionId);
      patchOfferStatus(lessonOptionId, updated.status);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.activatedSuccess', 'Lesson option activated'),
        severity: 'success',
      });
    } catch (err) {
      console.error(`Error activating lesson option ${lessonOptionId}:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.activateError', 'Failed to activate lesson option'),
        severity: 'error',
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleDeactivate = async (event: React.MouseEvent | null, lessonOptionId: string) => {
    event?.stopPropagation();
    closeMenu();
    try {
      setStatusLoadingId(lessonOptionId);
      const updated = await hostApiClient.deactivateLessonOption(lessonOptionId);
      patchOfferStatus(lessonOptionId, updated.status);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.deactivatedSuccess', 'Lesson option moved to draft'),
        severity: 'success',
      });
    } catch (err) {
      console.error(`Error deactivating lesson option ${lessonOptionId}:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.deactivateError', 'Failed to deactivate lesson option'),
        severity: 'error',
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleArchive = async (event: React.MouseEvent | null, lessonOptionId: string) => {
    event?.stopPropagation();
    closeMenu();
    const moved = offerOptions.find((option) => option.id === lessonOptionId);
    try {
      setStatusLoadingId(lessonOptionId);
      const updated = await hostApiClient.archiveLessonOption(lessonOptionId);
      setOfferOptions((prev) => prev.filter((option) => option.id !== lessonOptionId));
      if (moved) {
        setArchivedOptions((prev) => [
          { ...moved, status: updated.status },
          ...prev.filter((option) => option.id !== lessonOptionId),
        ]);
      }
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.archivedSuccess', 'Lesson option moved to Archive'),
        severity: 'success',
      });
    } catch (err) {
      console.error(`Error archiving lesson option ${lessonOptionId}:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.archiveError', 'Failed to archive lesson option'),
        severity: 'error',
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleRestore = async (event: React.MouseEvent | null, lessonOptionId: string) => {
    event?.stopPropagation();
    closeMenu();
    const moved = archivedOptions.find((option) => option.id === lessonOptionId);
    try {
      setStatusLoadingId(lessonOptionId);
      const updated = await hostApiClient.restoreLessonOption(lessonOptionId);
      setArchivedOptions((prev) => prev.filter((option) => option.id !== lessonOptionId));
      if (moved) {
        setOfferOptions((prev) => [
          { ...moved, status: updated.status },
          ...prev.filter((option) => option.id !== lessonOptionId),
        ]);
      }
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.restoredSuccess', 'Lesson option restored as draft'),
        severity: 'success',
      });
    } catch (err) {
      console.error(`Error restoring lesson option ${lessonOptionId}:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.restoreError', 'Failed to restore lesson option'),
        severity: 'error',
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await hostApiClient.removeLessonOption(removeTarget.id);
      setOfferOptions((prev) => prev.filter((option) => option.id !== removeTarget.id));
      setArchivedOptions((prev) => prev.filter((option) => option.id !== removeTarget.id));
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.removedSuccess', 'Lesson option removed'),
        severity: 'success',
      });
      setRemoveTarget(null);
    } catch (err) {
      console.error(`Error removing lesson option ${removeTarget.id}:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.removeError', 'Failed to remove lesson option'),
        severity: 'error',
      });
    } finally {
      setRemoving(false);
    }
  };

  const openRemoveDialog = (event: React.MouseEvent | null, option: LessonOptionSummaryResponse) => {
    event?.stopPropagation();
    closeMenu();
    setRemoveTarget(option);
  };

  const handleRowNavigate = (option: LessonOptionSummaryResponse) => {
    navigate(`/host/lesson-options/${option.id}`);
  };

  const handleEdit = (event: React.MouseEvent | null, option: LessonOptionSummaryResponse) => {
    event?.stopPropagation();
    closeMenu();
    navigate(`/host/lesson-options/${option.id}/edit`);
  };

  const handleDuplicate = async (event: React.MouseEvent | null, option: LessonOptionSummaryResponse) => {
    event?.stopPropagation();
    closeMenu();
    try {
      setStatusLoadingId(option.id);
      const detail = await hostApiClient.getLessonOption(option.id);
      const name = nextLessonOptionCopyName(detail.name, detail.sessionType, [
        ...offerOptions,
        ...archivedOptions,
      ]);
      navigate('/host/lesson-options/new', {
        state: {
          duplicateFrom: {
            name,
            description: detail.description,
            durationMinutes: detail.durationMinutes,
            price: detail.price,
            currencyCode: detail.currencyCode,
            sessionType: detail.sessionType,
            assignedTutorIds: detail.assignedTutorIds ?? [],
          },
        } satisfies LessonOptionDuplicateLocationState,
      });
    } catch (err) {
      console.error(`Error loading lesson option ${option.id} for duplicate:`, err);
      setSnackbar({
        open: true,
        message: t('hostLessonOptions.fetchDetailError', 'Failed to load this lesson option'),
        severity: 'error',
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const columns: GridColDef<LessonOptionSummaryResponse>[] = [
    {
      field: 'name',
      headerName: t('hostLessonOptions.columnOption', 'Lesson'),
      flex: 1.4,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => (
        <Box sx={{ py: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {params.row.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {t('hostLessonOptions.durationMinutes', '{{count}} min', {
              count: params.row.durationMinutes,
            })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'sessionType',
      headerName: t('hostLessonOptions.sessionType', 'Session'),
      width: 140,
      renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => (
        <TutorLessonSessionTypeChip sessionType={params.row.sessionType} />
      ),
    },
    {
      field: 'price',
      headerName: t('hostLessonOptions.price', 'Price'),
      flex: 0.9,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => (
        <Typography variant="body2" noWrap>
          {formatMoneyAmount(params.row.price, params.row.currencyCode, i18n.language)}
        </Typography>
      ),
    },
    ...(isIndividualTutorOrg
      ? []
      : [
          {
            field: 'assignedTutorCount',
            headerName: t('hostLessonOptions.assignedTutors', 'Tutors'),
            width: 160,
            renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => (
              <Typography variant="body2">
                {t('hostLessonOptions.assignedTutorCount', '{{count}} tutors', {
                  count: params.row.assignedTutorCount,
                })}
              </Typography>
            ),
          } satisfies GridColDef<LessonOptionSummaryResponse>,
        ]),
    {
      field: 'status',
      headerName: t('hostLessonOptions.status', 'Status'),
      width: 130,
      renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => (
        <LessonOptionStatusChip status={params.row.status} />
      ),
    },
    {
      field: 'actions',
      headerName: t('hostLessonOptions.actions', 'Actions'),
      width: 88,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<LessonOptionSummaryResponse>) => {
        if (statusLoadingId === params.row.id) {
          return <CircularProgress size={20} />;
        }
        return (
          <Tooltip title={t('hostLessonOptions.moreActions', 'More actions')}>
            <IconButton
              size="small"
              aria-label={t('hostLessonOptions.moreActions', 'More actions')}
              onClick={(event) => openMenu(event, params.row)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  if (loading && options.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1">
              {t('hostLessonOptions.pageTitle', 'Lessons')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isIndividualTutorOrg
                ? t(
                    'hostLessonOptions.pageSubtitleIndividualTutor',
                    'Catalog of bookable lessons for your tutoring profile.'
                  )
                : t(
                    'hostLessonOptions.pageSubtitle',
                    'Catalog of bookable lessons. Assign tutors on each option — tutors cannot edit these themselves.'
                  )}
            </Typography>
          </Box>
          {listTab === 'offers' ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/host/lesson-options/new')}
            >
              {t('hostLessonOptions.add', 'Add lesson')}
            </Button>
          ) : null}
        </Box>

        <Tabs
          value={listTab}
          onChange={(_event, value: ListTab) => setListTab(value)}
          sx={{ mb: 1 }}
        >
          <Tab
            value="offers"
            label={`${t('hostLessonOptions.tabCurrent', 'Current')} (${offerOptions.length})`}
          />
          <Tab
            value="archive"
            label={`${t('hostLessonOptions.tabArchive', 'Archive')} (${archivedOptions.length})`}
          />
        </Tabs>

        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {options.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {listTab === 'archive'
                ? t('hostLessonOptions.noArchivedOptions', 'No archived lessons')
                : t('hostLessonOptions.noOptions', 'No lessons yet')}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Stack spacing={2}>
            {options.map((option) => (
              <Card
                key={option.id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer' }}
                onClick={() => handleRowNavigate(option)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: '"IBM Plex Mono", "ui-monospace", monospace',
                        fontSize: '1.75rem',
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                        fontWeight: 600,
                      }}
                    >
                      {option.durationMinutes}′
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.75 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatMoneyAmount(option.price, option.currencyCode, i18n.language)}
                      {!isIndividualTutorOrg ? (
                        <>
                          {' · '}
                          {t('hostLessonOptions.assignedTutorCount', '{{count}} tutors', {
                            count: option.assignedTutorCount,
                          })}
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <LessonOptionStatusChip status={option.status} />
                    {statusLoadingId === option.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        aria-label={t('hostLessonOptions.moreActions', 'More actions')}
                        onClick={(event) => openMenu(event, option)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
                <Box sx={{ mt: 1.25 }}>
                  <TutorLessonSessionTypeChip sessionType={option.sessionType} />
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          <DataGrid
            autoHeight
            rows={options}
            columns={columns}
            getRowId={(row) => row.id}
            getRowHeight={() => 64}
            disableRowSelectionOnClick
            onRowClick={(params) => handleRowNavigate(params.row)}
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{
              border: 0,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
            }}
          />
        )}
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuOption && menuOption.status !== 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleEdit(event, menuOption)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit', 'Edit')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption?.status === 'INACTIVE' ? (
          <MenuItem onClick={(event) => handleActivate(event, menuOption.id)}>
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.activate', 'Activate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption?.status === 'ACTIVE' ? (
          <MenuItem onClick={(event) => handleDeactivate(event, menuOption.id)}>
            <ListItemIcon>
              <PauseCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.deactivate', 'Deactivate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption?.status === 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleRestore(event, menuOption.id)}>
            <ListItemIcon>
              <UnarchiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.restore', 'Restore')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption ? (
          <MenuItem onClick={(event) => handleDuplicate(event, menuOption)}>
            <ListItemIcon>
              <ContentCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.duplicate', 'Duplicate')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption && menuOption.status !== 'ARCHIVED' ? (
          <MenuItem onClick={(event) => handleArchive(event, menuOption.id)}>
            <ListItemIcon>
              <ArchiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.archive', 'Archive')}</ListItemText>
          </MenuItem>
        ) : null}
        {menuOption?.status === 'ARCHIVED' ? (
          <MenuItem onClick={(event) => openRemoveDialog(event, menuOption)}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('hostLessonOptions.remove', 'Remove')}</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog open={Boolean(removeTarget)} onClose={() => (!removing ? setRemoveTarget(null) : undefined)}>
        <DialogTitle>{t('hostLessonOptions.removeTitle', 'Remove this lesson?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'hostLessonOptions.removeConfirmBody',
              'It will disappear from Lessons and can’t be booked anymore. Existing bookings stay on the calendar.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove} disabled={removing}>
            {removing ? <CircularProgress size={20} /> : t('hostLessonOptions.remove', 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostLessonOptionsPage;
